import { type ReactNode } from "react";
import { IntersectionFade } from "../components/IntersectionFade";

interface PlayerCard {
  title: string;
  tag: string;
  desc: string;
  icon: ReactNode;
}

const PLAYERS: PlayerCard[] = [
  {
    title: "Web / PWA",
    tag: "Instant deploy",
    desc: "Runs in any Chromium-based browser. Install as a PWA for kiosk mode — no native binary required.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
  },
  {
    title: "Electron",
    tag: "Windows · macOS · Linux",
    desc: "Full kiosk shell with auto-launch, deep OS integration, and hardware-accelerated rendering on any desktop platform.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <polyline points="8 21 12 17 16 21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
  },
  {
    title: "Tauri",
    tag: "Tiny footprint",
    desc: "Rust-powered shell with a fraction of the Electron binary size. Perfect for bandwidth-constrained or embedded deployments.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5 12 2" />
        <circle cx="12" cy="12" r="4" />
      </svg>
    ),
  },
  {
    title: "Android TV",
    tag: "Android 8+",
    desc: "WebView-hosted player-core with a native kiosk APK. Runs on Fire TV, NVIDIA SHIELD, commercial Android displays, and more.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2" y="7" width="20" height="15" rx="2" />
        <polyline points="17 2 12 7 7 2" />
      </svg>
    ),
  },
  {
    title: "Raspberry Pi",
    tag: "Pi 4 / Pi 5",
    desc: "Chromium or WPE kiosk shell. Verified offline playback and full KSP sync on Pi OS Lite — a $50 signage node.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <rect x="9" y="9" width="6" height="6" rx="1" />
        <line x1="9" y1="1" x2="9" y2="4" />
        <line x1="15" y1="1" x2="15" y2="4" />
        <line x1="9" y1="20" x2="9" y2="23" />
        <line x1="15" y1="20" x2="15" y2="23" />
        <line x1="20" y1="9" x2="23" y2="9" />
        <line x1="20" y1="14" x2="23" y2="14" />
        <line x1="1" y1="9" x2="4" y2="9" />
        <line x1="1" y1="14" x2="4" y2="14" />
      </svg>
    ),
  },
];

export function Players() {
  return (
    <section className="section" id="players" aria-labelledby="players-heading">
      <div className="container">
        <IntersectionFade>
          <div className="section-header">
            <span className="eyebrow">Players</span>
            <h2 className="h2" id="players-heading">
              Every surface,{" "}
              <span className="grad-text">one codebase</span>
            </h2>
            <p className="lead">
              The same <code style={{ fontSize: "0.95em", color: "var(--accent)", fontFamily: "monospace" }}>@kynetra/player-core</code> engine
              runs across all platforms. Platform shells are thin wrappers — features land everywhere at once.
            </p>
          </div>
        </IntersectionFade>

        <div className="players-grid">
          {PLAYERS.map(({ title, tag, desc, icon }, i) => (
            <IntersectionFade key={title} delay={i * 80}>
              <article className="glass-card player-card">
                <div className="player-icon">{icon}</div>
                <h3 className="player-title">{title}</h3>
                <p className="player-desc">{desc}</p>
                <span className="player-tag">{tag}</span>
              </article>
            </IntersectionFade>
          ))}
        </div>
      </div>
    </section>
  );
}
